import { forwardRef, useState, useEffect, useCallback, type InputHTMLAttributes } from 'react'

import { cn } from '@elsapiens/utils'

export interface CurrencyInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value' | 'size'> {
  value?: number | null
  onChange?: (value: number | null) => void
  currency?: string
  locale?: string
  precision?: number
  min?: number
  max?: number
  allowNegative?: boolean
  error?: string
  hideMessage?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: 'el-input-sm',
  md: 'el-input-md',
  lg: 'el-input-lg',
}

function getCurrencySymbol(locale: string, currency: string): string {
  try {
    const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'symbol' })
    const parts = formatter.formatToParts(0)
    const symbolPart = parts.find(part => part.type === 'currency')
    /* v8 ignore next */
    return symbolPart?.value ?? '$'
  } catch {
    /* v8 ignore next */
    return '$'
  }
}

function formatForDisplay(num: number | null | undefined, locale: string, precision: number): string {
  if (num === null || num === undefined) { return '' }
  try {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: precision, maximumFractionDigits: precision }).format(num)
  } catch {
    return num.toFixed(precision)
  }
}

function parseDisplayValue(str: string, currencySymbol: string): number | null {
  if (!str) { return null }
  const cleaned = str.replace(currencySymbol, '').replace(/\s/g, '').replace(/,/g, '')
  const num = parseFloat(cleaned)
  /* v8 ignore next */
  return isNaN(num) ? null : num
}

function clampValue(numValue: number, min: number | undefined, max: number | undefined): number {
  let v = numValue
  if (min !== undefined && v < min) { v = min }
  if (max !== undefined && v > max) { v = max }
  return v
}

interface CurrencyHandlerInput {
  value: number | null | undefined
  setDisplayValue: React.Dispatch<React.SetStateAction<string>>
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>
  displayValue: string
  currencySymbol: string
  canBeNegative: boolean
  min?: number
  max?: number
  format: (n: number | null | undefined) => string
  onChange?: (value: number | null) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
}

function buildCurrencyHandlers(input: CurrencyHandlerInput): {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleFocus: (e: React.FocusEvent<HTMLInputElement>) => void
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void
} {
  const { value, setDisplayValue, setIsFocused, displayValue, currencySymbol, canBeNegative, min, max, format, onChange, onBlur, onFocus } = input
  return {
    handleChange: (e) => {
      const rawValue = e.target.value
      const validPattern = canBeNegative ? /^-?[\d.,]*$/ : /^[\d.,]*$/
      if (!validPattern.test(rawValue.replace(currencySymbol, '').replace(/\s/g, ''))) { return }
      setDisplayValue(rawValue)
      const numValue = parseDisplayValue(rawValue, currencySymbol)
      if (numValue === null) { onChange?.(null); return }
      /* v8 ignore next */
      if (!canBeNegative && numValue < 0) { return }
      if (min !== undefined && numValue < min) { return }
      if (max !== undefined && numValue > max) { return }
      onChange?.(numValue)
    },
    handleFocus: (e) => {
      setIsFocused(true)
      if (value !== null && value !== undefined) { setDisplayValue(value.toString()) }
      onFocus?.(e)
    },
    handleBlur: (e) => {
      setIsFocused(false)
      const numValue = parseDisplayValue(displayValue, currencySymbol)
      if (numValue !== null) {
        const finalValue = clampValue(numValue, min, max)
        onChange?.(finalValue)
        setDisplayValue(format(finalValue))
      } else {
        setDisplayValue('')
      }
      onBlur?.(e)
    },
  }
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      value, onChange, currency = 'INR', locale = 'en-US', precision = 2, min, max,
      allowNegative = false, error, hideMessage = false, size = 'md', className, disabled, onBlur, onFocus, ...props
    },
    ref,
  ) => {
    const [displayValue, setDisplayValue] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const currencySymbol = getCurrencySymbol(locale, currency)
    const format = useCallback((num: number | null | undefined) => formatForDisplay(num, locale, precision), [locale, precision])

    useEffect(() => {
      if (!isFocused) { setDisplayValue(format(value)) }
    }, [value, isFocused, format])

    const handlers = buildCurrencyHandlers({
      value, setDisplayValue, setIsFocused, displayValue, currencySymbol,
      canBeNegative: allowNegative, min, max, format, onChange, onBlur, onFocus,
    })

    return (
      <div className="w-full">
        <div className="relative">
          <div className="el-input-prefix-icon">{currencySymbol}</div>
          <input
            ref={ref}
            className={cn('el-input el-numeric-input el-input-has-prefix-narrow', sizeClasses[size], error && 'el-input-invalid', className)}
            disabled={disabled} inputMode="decimal" type="text" value={displayValue}
            onBlur={handlers.handleBlur} onChange={handlers.handleChange} onFocus={handlers.handleFocus}
            {...props}
          />
        </div>
        {!hideMessage && error && (
          <div className="el-field-message"><p className="el-field-error">{error}</p></div>
        )}
      </div>
    )
  }
)

CurrencyInput.displayName = 'CurrencyInput'
